import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  cryptoPortfolioService,
  GrowthChallenge,
} from '../../services/cryptoPortfolioService';
import GrowthChallengeWizard from './GrowthChallengeWizard';

const POLL_MS = 60_000;

function fmtUsd(n: number | null | undefined): string {
  const v = Number(n ?? 0);
  if (!Number.isFinite(v)) return '—';
  const sign = v < 0 ? '-' : '';
  return `${sign}$${Math.abs(v).toFixed(2)}`;
}

function statusMeta(status: string): { label: string; color: string; bg: string } {
  switch (status) {
    case 'ACTIVE':
      return { label: 'Active', color: '#15803d', bg: '#dcfce7' };
    case 'PAUSED':
      return { label: 'Paused', color: '#b45309', bg: '#fef3c7' };
    case 'TARGET_REACHED':
      return { label: 'Target reached', color: '#1d4ed8', bg: '#dbeafe' };
    case 'STOPPED_FLOOR':
      return { label: 'Stopped at floor', color: '#b91c1c', bg: '#fee2e2' };
    case 'STOPPED_MANUAL':
      return { label: 'Stopped', color: '#475569', bg: '#f1f5f9' };
    default:
      return { label: status, color: '#475569', bg: '#f1f5f9' };
  }
}

function isLive(challenge: GrowthChallenge): boolean {
  return challenge.status === 'ACTIVE' || challenge.status === 'PAUSED';
}

interface GrowthChallengeCardProps {
  canManage: boolean;
  cardStyle: React.CSSProperties;
  onChallengeBotIdsChange?: (botIds: number[]) => void;
}

const GrowthChallengeCard: React.FC<GrowthChallengeCardProps> = ({
  canManage,
  cardStyle,
  onChallengeBotIdsChange,
}) => {
  const [challenges, setChallenges] = useState<GrowthChallenge[]>([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [wizardOpen, setWizardOpen] = useState(false);

  /** Every live challenge; if none are live, the most recent ended one for reference. */
  const displayChallenges = useMemo(() => {
    const live = challenges.filter(isLive);
    if (live.length > 0) return live;
    return challenges.length > 0 ? [challenges[0]] : [];
  }, [challenges]);

  const notifyBotIds = useCallback((list: GrowthChallenge[]) => {
    if (!onChallengeBotIdsChange) return;
    const ids: number[] = [];
    for (const c of list) {
      if (isLive(c)) {
        for (const b of c.bots ?? []) ids.push(b.botId);
      }
    }
    onChallengeBotIdsChange(ids);
  }, [onChallengeBotIdsChange]);

  const load = useCallback(async () => {
    try {
      const list = await cryptoPortfolioService.listGrowthChallenges();
      setChallenges(list);
      notifyBotIds(list);
      setError(null);
    } catch (e: any) {
      setError(e?.message || 'Failed to load challenges');
    } finally {
      setLoading(false);
    }
  }, [notifyBotIds]);

  useEffect(() => {
    void load();
    const timer = window.setInterval(() => void load(), POLL_MS);
    return () => window.clearInterval(timer);
  }, [load]);

  const runAction = async (challengeId: number, action: () => Promise<GrowthChallenge>) => {
    setBusyId(challengeId);
    setError(null);
    try {
      await action();
      await load();
    } catch (e: any) {
      setError(e?.message || 'Action failed');
    } finally {
      setBusyId(null);
    }
  };

  const actionButton = (
    label: string,
    onClick: () => void,
    busy: boolean,
    variant: 'primary' | 'danger' | 'ghost' = 'ghost',
  ) => (
    <button
      key={label}
      type="button"
      onClick={onClick}
      disabled={busy}
      style={{
        padding: '6px 12px',
        borderRadius: 999,
        border: variant === 'ghost' ? '1px solid #cbd5e1' : 'none',
        background: variant === 'primary' ? '#086afb' : variant === 'danger' ? '#dc2626' : '#fff',
        color: variant === 'ghost' ? '#334155' : '#fff',
        fontSize: 12,
        fontWeight: 700,
        cursor: busy ? 'wait' : 'pointer',
        opacity: busy ? 0.7 : 1,
      }}
    >
      {label}
    </button>
  );

  const renderChallenge = (challenge: GrowthChallenge, index: number) => {
    const busy = busyId === challenge.id;
    const equity = challenge.equityUsd ?? challenge.startBalanceUsd ?? 0;
    const start = challenge.startBalanceUsd ?? 0;
    const target = challenge.targetBalanceUsd ?? 0;
    const floor = challenge.floorBalanceUsd ?? 0;
    const progress = challenge.progressPct != null
      ? Math.max(0, Math.min(100, Number(challenge.progressPct)))
      : 0;
    const meta = statusMeta(challenge.status);

    return (
      <div
        key={challenge.id}
        style={{
          marginTop: 14,
          paddingTop: index > 0 ? 14 : 0,
          borderTop: index > 0 ? '1px solid #e2e8f0' : 'none',
        }}
      >
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, alignItems: 'center', justifyContent: 'space-between' }}>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, alignItems: 'center' }}>
            <span style={{ fontWeight: 800, fontSize: 15, color: '#1e293b' }}>{challenge.name || `Challenge #${challenge.id}`}</span>
            <span style={{ padding: '3px 10px', borderRadius: 999, background: meta.bg, color: meta.color, fontSize: 11, fontWeight: 700 }}>
              {meta.label}
            </span>
            {challenge.daysElapsed != null && (
              <span style={{ fontSize: 12, color: '#64748b', fontWeight: 600 }}>
                Day {Number(challenge.daysElapsed) + 1}
              </span>
            )}
            {challenge.kucoinAccountName && (
              <span style={{ fontSize: 12, color: '#94a3b8' }}>{challenge.kucoinAccountName}</span>
            )}
          </div>
          {canManage && (
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              {challenge.status === 'ACTIVE' && (
                <>
                  {actionButton('Refresh', () => void runAction(challenge.id, () => cryptoPortfolioService.evaluateGrowthChallenge(challenge.id)), busy)}
                  {actionButton('Pause', () => void runAction(challenge.id, () => cryptoPortfolioService.pauseGrowthChallenge(challenge.id)), busy)}
                  {actionButton('Stop & close all', () => {
                    if (window.confirm(`Stop "${challenge.name || `Challenge #${challenge.id}`}" and close every open position it manages?`)) {
                      void runAction(challenge.id, () => cryptoPortfolioService.stopGrowthChallenge(challenge.id));
                    }
                  }, busy, 'danger')}
                </>
              )}
              {challenge.status === 'PAUSED' && (
                <>
                  {actionButton('Resume', () => void runAction(challenge.id, () => cryptoPortfolioService.resumeGrowthChallenge(challenge.id)), busy, 'primary')}
                  {actionButton('Stop & close all', () => {
                    if (window.confirm(`Stop "${challenge.name || `Challenge #${challenge.id}`}" and close every open position it manages?`)) {
                      void runAction(challenge.id, () => cryptoPortfolioService.stopGrowthChallenge(challenge.id));
                    }
                  }, busy, 'danger')}
                </>
              )}
            </div>
          )}
        </div>

        {challenge.statusNote && (
          <div style={{ marginTop: 8, fontSize: 12, color: '#b45309' }}>{challenge.statusNote}</div>
        )}

        <div style={{ marginTop: 12 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, color: '#64748b', fontWeight: 600 }}>
            <span>{fmtUsd(start)} start</span>
            <span style={{ color: '#1e293b', fontWeight: 800, fontSize: 14 }}>{fmtUsd(equity)} equity</span>
            <span>{fmtUsd(target)} target</span>
          </div>
          <div style={{ marginTop: 6, height: 10, borderRadius: 999, background: '#e2e8f0', overflow: 'hidden' }}>
            <div
              style={{
                width: `${progress}%`,
                height: '100%',
                borderRadius: 999,
                background: challenge.status === 'STOPPED_FLOOR'
                  ? '#dc2626'
                  : challenge.status === 'TARGET_REACHED'
                    ? '#16a34a'
                    : 'linear-gradient(90deg, #086afb, #16a34a)',
                transition: 'width 300ms ease',
              }}
            />
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 14, marginTop: 8, fontSize: 12, color: '#475569' }}>
            <span>Locked profit: <strong style={{ color: '#15803d' }}>{fmtUsd(challenge.lockedProfitUsd)}</strong></span>
            <span>At-risk equity: <strong>{fmtUsd(challenge.riskEquityUsd)}</strong></span>
            <span>Floor: <strong style={{ color: '#b91c1c' }}>{fmtUsd(floor)}</strong></span>
            {challenge.realPnlUsd != null && (
              <span title="KuCoin account equity change since the challenge started — includes trading fees and funding. Accurate when the challenge runs on its own sub-account.">
                Real P&L after fees:{' '}
                <strong style={{ color: Number(challenge.realPnlUsd) >= 0 ? '#15803d' : '#b91c1c' }}>
                  {Number(challenge.realPnlUsd) >= 0 ? '+' : ''}{fmtUsd(challenge.realPnlUsd)}
                </strong>
              </span>
            )}
          </div>
        </div>

        {(challenge.ladder?.length ?? 0) > 0 && (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 10 }}>
            {challenge.ladder!.map((step, idx) => (
              <span
                key={idx}
                style={{
                  padding: '3px 10px',
                  borderRadius: 999,
                  fontSize: 11,
                  fontWeight: 700,
                  background: step.reached ? '#dcfce7' : '#f1f5f9',
                  color: step.reached ? '#15803d' : '#64748b',
                  border: step.reached ? '1px solid #86efac' : '1px solid #e2e8f0',
                }}
              >
                {step.reached ? '✓ ' : ''}{fmtUsd(step.atUsd)} → lock {fmtUsd(step.lockUsd)}
              </span>
            ))}
          </div>
        )}

        {(challenge.bots?.length ?? 0) > 0 && (
          <div style={{ marginTop: 12, display: 'grid', gap: 6 }}>
            {challenge.bots!.map(bot => {
              const pnl = Number(bot.netPnlUsd ?? 0);
              return (
                <div
                  key={bot.botId}
                  style={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    gap: 10,
                    alignItems: 'center',
                    padding: '8px 12px',
                    borderRadius: 10,
                    border: '1px solid #e2e8f0',
                    background: '#f8fafc',
                    fontSize: 12,
                  }}
                >
                  <span style={{ fontWeight: 700, color: '#1e293b' }}>{bot.sectorLabel || bot.sectorId}</span>
                  <span
                    style={{
                      padding: '2px 8px',
                      borderRadius: 999,
                      fontSize: 10,
                      fontWeight: 700,
                      background: (bot.status === 'RUNNING' || ((bot.openLegs ?? 0) > 0 && bot.status === 'COMPLETED'))
                        ? (bot.status === 'RUNNING' ? '#dcfce7' : '#f3e8ff')
                        : '#f1f5f9',
                      color: bot.status === 'RUNNING'
                        ? '#15803d'
                        : ((bot.openLegs ?? 0) > 0 && bot.status === 'COMPLETED')
                          ? '#7e22ce'
                          : '#64748b',
                    }}
                  >
                    {bot.status === 'RUNNING'
                      ? 'RUNNING'
                      : ((bot.openLegs ?? 0) > 0 && bot.status === 'COMPLETED')
                        ? 'MONITORING'
                        : bot.status}
                  </span>
                  <span style={{ color: '#64748b' }}>{fmtUsd(bot.amountUsd)}/leg</span>
                  <span style={{ color: '#64748b' }}>{bot.openLegs ?? 0} open</span>
                  <span style={{ fontWeight: 700, color: pnl >= 0 ? '#15803d' : '#b91c1c' }}>
                    {pnl >= 0 ? '+' : ''}{fmtUsd(pnl)}
                  </span>
                  {bot.lastError && (
                    <span style={{ color: '#b45309', fontSize: 11, flexBasis: '100%' }}>{bot.lastError}</span>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    );
  };

  return (
    <div style={{ ...cardStyle, marginTop: 12 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12, flexWrap: 'wrap' }}>
        <div>
          <div style={{ fontSize: 12, color: '#64748b', fontWeight: 600 }}>Growth Challenges</div>
          <div style={{ fontSize: 13, color: '#94a3b8', marginTop: 4 }}>
            Multi-sector bots compounding capped bankrolls with profit-locks and a floor kill-switch — one live challenge per KuCoin sub-account
          </div>
        </div>
        {canManage && (
          <button
            type="button"
            onClick={() => setWizardOpen(true)}
            style={{
              padding: '6px 14px',
              borderRadius: 999,
              border: 'none',
              background: '#086afb',
              color: '#fff',
              fontSize: 12,
              fontWeight: 700,
              cursor: 'pointer',
            }}
          >
            + Start challenge
          </button>
        )}
      </div>

      {error && (
        <div style={{ marginTop: 12, padding: '10px 12px', background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 8, color: '#b91c1c', fontSize: 12 }}>
          {error}
        </div>
      )}

      {loading ? (
        <p style={{ margin: '14px 0 0', fontSize: 13, color: '#94a3b8' }}>Loading challenges…</p>
      ) : displayChallenges.length === 0 ? (
        <p style={{ margin: '14px 0 0', fontSize: 13, color: '#64748b' }}>
          No challenge yet. Start one to spin up a bot per sector (1 or more) that compounds a small bankroll
          (e.g. $10 → $100) with automatic profit-locks and a floor kill-switch. High risk — only use money you
          can afford to lose.
        </p>
      ) : (
        displayChallenges.map((challenge, index) => renderChallenge(challenge, index))
      )}

      <GrowthChallengeWizard
        open={wizardOpen}
        onClose={() => setWizardOpen(false)}
        onCreated={() => void load()}
      />
    </div>
  );
};

export default GrowthChallengeCard;
